import { useState, type FormEvent } from 'react';
import { api, errorText, type Me } from '../api';
import { useAuth } from '../auth';
import { Btn, Field } from '../ui/kit';
import markLight from '../assets/vmm-mark-light.png';
import markDark from '../assets/vmm-mark-dark.png';

function Art() {
  return (
    <div className="auth-art">
      <div className="lh-band"><img src={markLight} alt="" /><div className="lh-name">VMM<br />Engenharia e Consultoria</div></div>
      <img className="lh-water" src={markDark} alt="" />
      <h1>Gestão integrada de projetos siderúrgicos</h1>
      <p>Planejamento, documentos, avanço físico e medições do projeto em um só lugar, com registro de quem fez cada alteração.</p>
      <div className="specs"><span>EAP</span><span>Estrutura analítica e importação de planilhas</span><span>CPM</span><span>Caminho crítico, folgas e linhas de base</span><span>LOG</span><span>Trilha de auditoria imutável</span></div>
      <div className="lh-foot" aria-hidden="true" />
    </div>
  );
}
/** Marca VMM (versão clara, para fundos escuros). */
export function Mark() {
  return <img className="brand-mark" src={markLight} alt="VMM" />;
}

export function LoginPage({ expired }: { expired?: boolean }) {
  const { login } = useAuth();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState(''); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try { await login(email, password); } catch (x) { setErr(errorText(x)); } finally { setBusy(false); }
  };
  return (
    <div className="auth">
      <Art />
      <div className="auth-form">
        <form className="auth-card" onSubmit={submit} noValidate>
          <h2>Entrar</h2>
          {expired && <div className="callout info">Sua sessão terminou. Entre de novo para continuar.</div>}
          <Field label="E-mail"><input className="input" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required /></Field>
          <Field label="Senha"><input className="input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required /></Field>
          {err && <div className="callout crit" role="alert">{err}</div>}
          <Btn type="submit" className="btn primary" why={busy ? 'Aguarde: entrando.' : (!email || !password) && 'Preencha o e-mail e a senha.'}>{busy ? 'Entrando...' : 'Entrar'}</Btn>
          <p className="small muted">Esqueceu a senha? Peça ao administrador da sua empresa uma senha temporária.</p>
          {import.meta.env.MODE === 'demo' && <DemoUsers pick={(e, p) => { setEmail(e); setPassword(p); }} />}
        </form>
      </div>
    </div>
  );
}

export function SetupPage() {
  const { refresh, login } = useAuth();
  const [f, setF] = useState({ org: '', cnpj: '', name: '', email: '', password: '', confirm: '', token: '' });
  const { state } = useAuth(); const needsToken = state.status === 'setup' && !!state.needsToken;
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF(o => ({ ...o, [k]: e.target.value }));
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    if (f.password !== f.confirm) { setErr('As senhas não conferem.'); return; }
    setBusy(true);
    try {
      await api.post<Me>('/api/auth/bootstrap', { organization: { name: f.org, cnpj: f.cnpj || undefined }, admin: { name: f.name, email: f.email, password: f.password }, ...(needsToken ? { setupToken: f.token.trim() } : {}) });
      await login(f.email, f.password);
    } catch (x) { setErr(errorText(x)); await refresh().catch(() => null); } finally { setBusy(false); }
  };
  return (
    <div className="auth">
      <Art />
      <div className="auth-form">
        <form className="auth-card" onSubmit={submit} noValidate>
          <h2>Primeiro acesso</h2>
          <p className="muted small">Cadastre a empresa dona da instalação e o administrador da plataforma. Esta tela só aparece uma vez.</p>
          {needsToken && <Field label="Código de instalação" required help="Fornecido por quem instalou o servidor (variável SETUP_TOKEN)."><input className="input mono" autoComplete="off" value={f.token} onChange={set('token')} /></Field>}
          <Field label="Empresa dona" required><input className="input" value={f.org} onChange={set('org')} /></Field>
          <Field label="CNPJ"><input className="input mono" value={f.cnpj} onChange={set('cnpj')} /></Field>
          <Field label="Seu nome" required><input className="input" autoComplete="name" value={f.name} onChange={set('name')} /></Field>
          <Field label="E-mail" required><input className="input" type="email" autoComplete="username" value={f.email} onChange={set('email')} /></Field>
          <Field label="Senha" required help="Mínimo de 10 caracteres, com letras e números."><input className="input" type="password" autoComplete="new-password" value={f.password} onChange={set('password')} /></Field>
          <Field label="Confirme a senha" required><input className="input" type="password" autoComplete="new-password" value={f.confirm} onChange={set('confirm')} /></Field>
          {err && <div className="callout crit" role="alert">{err}</div>}
          <Btn type="submit" className="btn primary" why={busy && 'Aguarde.'}>{busy ? 'Configurando...' : 'Configurar plataforma'}</Btn>
        </form>
      </div>
    </div>
  );
}

export function ChangePasswordForm({ forced, onDone }: { forced?: boolean; onDone: () => void }) {
  const [f, setF] = useState({ current: '', next: '', confirm: '' }); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    if (f.next !== f.confirm) { setErr('A confirmação não confere com a nova senha.'); return; }
    setBusy(true);
    try { await api.post('/api/auth/password', { current: f.current, next: f.next }); onDone(); } catch (x) { setErr(errorText(x)); } finally { setBusy(false); }
  };
  return (
    <form className="stack" onSubmit={submit} noValidate>
      {forced && <div className="callout info">Você entrou com uma senha temporária. Defina uma senha pessoal para continuar.</div>}
      <Field label="Senha atual" required><input className="input" type="password" autoComplete="current-password" value={f.current} onChange={e => setF({ ...f, current: e.target.value })} /></Field>
      <Field label="Nova senha" required help="Mínimo de 10 caracteres, com letras e números. Suas outras sessões serão encerradas."><input className="input" type="password" autoComplete="new-password" value={f.next} onChange={e => setF({ ...f, next: e.target.value })} /></Field>
      <Field label="Confirme a nova senha" required><input className="input" type="password" autoComplete="new-password" value={f.confirm} onChange={e => setF({ ...f, confirm: e.target.value })} /></Field>
      {err && <div className="callout crit" role="alert">{err}</div>}
      <div className="form-actions"><Btn type="submit" className="btn primary" why={busy && 'Aguarde.'}>{busy ? 'Salvando...' : 'Salvar nova senha'}</Btn></div>
    </form>
  );
}

export function ForcedPasswordPage() {
  const { refresh, logout } = useAuth();
  return (
    <div className="auth">
      <Art />
      <div className="auth-form"><div className="auth-card"><h2>Nova senha</h2><ChangePasswordForm forced onDone={() => void refresh()} /><button className="btn ghost" onClick={() => void logout()}>Sair</button></div></div>
    </div>
  );
}

const DEMO_USERS = [
  { email: 'ana@vmm.demo', label: 'Ana Gestora', role: 'administradora e gerente do projeto' },
  { email: 'diego@vmm.demo', label: 'Diego Diretor', role: 'diretor de engenharia' },
  { email: 'fatima@vmm.demo', label: 'Fátima Financeiro', role: 'diretora financeira' },
  { email: 'paulo@alfa.demo', label: 'Paulo Planejador', role: 'contratada, perfil planejador' },
  { email: 'rui@vmm.demo', label: 'Rui Mecânico', role: 'chefe da disciplina Mecânica' },
  { email: 'clara@siderurgica.demo', label: 'Clara (cliente)', role: 'pode ver, baixar, comentar e aprovar' },
  { email: 'bruno@siderurgica.demo', label: 'Bruno (cliente)', role: 'só pode visualizar' }
];
function DemoUsers({ pick }: { pick: (email: string, password: string) => void }) {
  return (
    <div className="callout info">
      <strong>Versão de demonstração.</strong> Escolha uma pessoa para entrar com o perfil dela (senha de todas: <span className="mono">Demo2026sp</span>):
      <div className="stack" style={{ gap: 6, marginTop: 8 }}>
        {DEMO_USERS.map(u => <button type="button" key={u.email} className="btn sm" style={{ justifyContent: 'flex-start', whiteSpace: 'normal', textAlign: 'left' }} onClick={() => pick(u.email, 'Demo2026sp')}><strong>{u.label}</strong><span className="muted">{u.role}</span></button>)}
      </div>
    </div>
  );
}
